import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Menu, X, Trophy, User, Search, Flame, ChevronDown } from 'lucide-react';
import { Show, useUser } from '@clerk/react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GAME_TYPES } from '@/pages/Games';

const SPORTS = [
  { key: 'NFL', name: 'Football (NFL)' },
  { key: 'NBA', name: 'Basketball (NBA)' },
  { key: 'MLB', name: 'Baseball (MLB)' },
  { key: 'NHL', name: 'Hockey (NHL)' },
  { key: 'EPL', name: 'Soccer (Premier League)' },
  { key: 'MIXED', name: 'Mixed Sports' },
];

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const { user } = useUser();

  const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');

  const tickerItems = [
    'DAILY CONTEST = 2X POINTS',
    'ONE SHOT PER DAY — MAKE IT COUNT',
    'PLAY DAILIES TO BUILD YOUR STREAK & EARN BADGES',
    'NEW PUZZLES EVERY DAY',
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-primary/20 bg-background/90 backdrop-blur-xl shadow-[0_4px_30px_-10px_rgba(0,217,255,0.15)]">
      {/* Solid background (no blur) — animating inside a backdrop-blur layer
          forces a full re-blur every frame and tanks scroll performance. */}
      <div className="overflow-hidden border-b border-secondary/30 bg-[hsl(240,15%,7%)]" data-testid="banner-daily-ticker">
        <div className="animate-marquee flex w-max items-center gap-8 py-1.5 pr-8 will-change-transform">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center gap-8" aria-hidden={copy === 1}>
              {tickerItems.map((item) => (
                <span key={item} className="flex items-center gap-2 whitespace-nowrap text-[10px] font-bold font-mono uppercase tracking-widest text-secondary">
                  <Flame className="h-3 w-3" />
                  {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-3 group relative">
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full group-hover:bg-primary/40 transition-colors duration-500 pointer-events-none" />
              <img
                src={`${import.meta.env.BASE_URL}logo.png`}
                alt="BoredFan"
                className="relative h-11 w-auto drop-shadow-[0_0_8px_rgba(0,217,255,0.3)]"
              />
            </Link>

            <div className="hidden md:flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={location === '/games' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`font-bold tracking-widest uppercase text-xs ${location === '/games' ? 'bg-primary/10 text-primary border border-primary/20 glow-primary' : 'text-muted-foreground hover:text-white hover:bg-white/5'} transition-all`}
                    data-testid="link-games"
                  >
                    Games <ChevronDown className="ml-1 h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 rounded-none border-primary/30 bg-background/95 backdrop-blur-xl">
                  <DropdownMenuItem asChild>
                    <Link href="/games" className="w-full cursor-pointer font-bold uppercase tracking-widest text-xs" data-testid="menu-all-games">
                      All Games
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-primary/20" />
                  <DropdownMenuLabel className="font-mono uppercase tracking-widest text-[10px] text-primary">By Sport</DropdownMenuLabel>
                  {SPORTS.map((s) => (
                    <DropdownMenuItem key={s.key} asChild>
                      <Link href={`/games?sport=${s.key}`} className="w-full cursor-pointer text-sm" data-testid={`menu-sport-${s.key}`}>
                        {s.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className="bg-primary/20" />
                  <DropdownMenuLabel className="font-mono uppercase tracking-widest text-[10px] text-secondary">By Game Type</DropdownMenuLabel>
                  {GAME_TYPES.map((t) => (
                    <DropdownMenuItem key={t.key} asChild>
                      <Link href={`/games?type=${t.key}`} className="w-full cursor-pointer text-sm" data-testid={`menu-type-${t.key}`}>
                        {t.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link href="/leaderboards">
                <Button
                  variant={location === '/leaderboards' ? 'secondary' : 'ghost'}
                  size="sm"
                  className={`font-bold tracking-widest uppercase text-xs ${location === '/leaderboards' ? 'bg-secondary/10 text-secondary border border-secondary/20 glow-secondary' : 'text-muted-foreground hover:text-white hover:bg-white/5'} transition-all`}
                  data-testid="link-leaderboards"
                >
                  <Trophy className={`mr-1.5 h-4 w-4 ${location === '/leaderboards' ? 'text-secondary' : ''}`} />
                  Leaderboards
                </Button>
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <Show when="signed-out">
              <Link href="/sign-in">
                <Button variant="ghost" size="sm" className="font-bold tracking-widest uppercase text-xs text-muted-foreground hover:text-white hover:bg-white/5" data-testid="button-sign-in">
                  Login
                </Button>
              </Link>
              <Link href="/sign-up">
                <Button size="sm" className="glow-primary font-bold tracking-widest uppercase text-xs bg-primary text-primary-foreground hover:bg-primary/90 border border-primary/50" data-testid="button-sign-up">
                  Get Started
                </Button>
              </Link>
            </Show>
            <Show when="signed-in">
              <Link href="/profile">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 border-primary/20 bg-background/50 hover:bg-primary/10 hover:border-primary/50 transition-all font-mono rounded-full px-2"
                  data-testid="link-profile"
                >
                  <Avatar className="h-6 w-6 border border-primary/30">
                    <AvatarImage src={user?.imageUrl} alt={user?.username || ''} />
                    <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
                      {user?.username?.[0]?.toUpperCase() || 'P1'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-bold text-xs tracking-wider text-primary">{user?.username}</span>
                </Button>
              </Link>
            </Show>
          </div>

          <button
            type="button"
            className="md:hidden rounded-lg p-2 text-primary hover:bg-primary/10 border border-transparent hover:border-primary/20 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6 drop-shadow-[0_0_5px_rgba(0,217,255,0.5)]" /> : <Menu className="h-6 w-6 drop-shadow-[0_0_5px_rgba(0,217,255,0.5)]" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="border-t border-primary/20 bg-background/95 backdrop-blur-md md:hidden animate-slide-up shadow-[0_20px_40px_rgba(0,0,0,0.8)]">
          <div className="space-y-2 px-4 py-6">
            <Link href="/games">
              <Button
                variant={location === '/games' ? 'secondary' : 'ghost'}
                className={`w-full justify-start font-bold uppercase tracking-widest ${location === '/games' ? 'bg-primary/10 text-primary border border-primary/20 glow-primary' : 'text-muted-foreground'}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="link-games-mobile"
              >
                All Games
              </Button>
            </Link>
            <div className="px-3 pt-1 pb-0.5 font-mono uppercase tracking-widest text-[10px] text-primary">By Sport</div>
            <div className="flex flex-wrap gap-2 px-3 pb-2">
              {SPORTS.map((s) => (
                <Link key={s.key} href={`/games?sport=${s.key}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none border-primary/30 text-primary text-xs uppercase tracking-wider"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`menu-sport-${s.key}-mobile`}
                  >
                    {s.key === 'MIXED' ? 'Mixed' : s.key}
                  </Button>
                </Link>
              ))}
            </div>
            <div className="px-3 pt-1 pb-0.5 font-mono uppercase tracking-widest text-[10px] text-secondary">By Game Type</div>
            <div className="flex flex-wrap gap-2 px-3 pb-2">
              {GAME_TYPES.map((t) => (
                <Link key={t.key} href={`/games?type=${t.key}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none border-secondary/30 text-secondary text-xs uppercase tracking-wider"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`menu-type-${t.key}-mobile`}
                  >
                    {t.name}
                  </Button>
                </Link>
              ))}
            </div>
            <Link href="/leaderboards">
              <Button
                variant={location === '/leaderboards' ? 'secondary' : 'ghost'}
                className={`w-full justify-start font-bold uppercase tracking-widest ${location === '/leaderboards' ? 'bg-secondary/10 text-secondary border border-secondary/20 glow-secondary' : 'text-muted-foreground'}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="link-leaderboards-mobile"
              >
                <Trophy className="mr-2 h-4 w-4" />
                Leaderboards
              </Button>
            </Link>
            <Show when="signed-in">
              <Link href="/profile">
                <Button
                  variant="ghost"
                  className="w-full justify-start font-bold uppercase tracking-widest text-muted-foreground hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="link-profile-mobile"
                >
                  <User className="mr-2 h-4 w-4" />
                  Player Profile
                </Button>
              </Link>
            </Show>
            <Show when="signed-out">
              <div className="flex flex-col gap-3 pt-4 mt-4 border-t border-border/50">
                <Link href="/sign-in" className="w-full">
                  <Button
                    variant="outline"
                    className="w-full font-bold uppercase tracking-widest border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="button-sign-in-mobile"
                  >
                    Login
                  </Button>
                </Link>
                <Link href="/sign-up" className="w-full">
                  <Button
                    className="w-full font-bold uppercase tracking-widest glow-primary bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="button-sign-up-mobile"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </Show>
          </div>
        </div>
      )}
    </nav>
  );
}
